import React from 'react';
import { styled } from '@linaria/react';

import { clamp } from '@/utils';
import VisuallyHidden from '@/components/VisuallyHidden';

const ROOT = '/images/interactive-guide-to-flexbox';

const LIMITS = {
  red: {
    min: -116,
    max: 25,
  },
  green: {
    min: -130,
    max: 25,
  },
  purple: {
    min: -122,
    max: 25,
  },
};

function Wiener({ color, initialPosition = '12%', onInteract }) {
  const [translateBy, setTranslateBy] = React.useState(0);
  const mouseDownPosition = React.useRef(null);

  const lockedTranslateBy = React.useRef(0);

  const translateByRef = React.useRef();
  translateByRef.current = translateBy;

  const handleDrag = React.useCallback(
    (ev) => {
      // This event handles both mouseMove and touchMove.
      let y;
      if (ev.touches) {
        ev.preventDefault();
        const touch = ev.touches[0];
        y = touch.clientY;
      } else {
        y = ev.clientY;
      }

      if (!mouseDownPosition.current) {
        return;
      }

      let deltaY =
        y - mouseDownPosition.current.y + lockedTranslateBy.current;

      deltaY = clamp(deltaY, LIMITS[color].min, LIMITS[color].max);

      setTranslateBy(deltaY);
    },
    [color]
  );

  const handleRelease = React.useCallback(() => {
    mouseDownPosition.current = null;
    lockedTranslateBy.current = translateByRef.current;

    window.removeEventListener('mousemove', handleDrag);
  }, [handleDrag]);

  const handleStart = React.useCallback(
    (ev) => {
      // This event handles both mouseMove and touchMove.
      let x, y;
      if (ev.touches) {
        ev.preventDefault();
        const touch = ev.touches[0];
        [x, y] = [touch.clientX, touch.clientY];
      } else {
        [x, y] = [ev.clientX, ev.clientY];
      }

      mouseDownPosition.current = { x, y };

      onInteract();

      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('touchmove', handleDrag);
      window.addEventListener('mouseup', handleRelease);
      window.addEventListener('touchend', handleRelease);
    },
    [onInteract, handleDrag, handleRelease]
  );

  const reset = React.useCallback(() => {
    setTranslateBy(0);
    mouseDownPosition.current = null;
    lockedTranslateBy.current = 0;
  }, []);

  const handleKeyDown = React.useCallback(
    (ev) => {
      if (ev.code === 'Enter') {
        reset();
        return;
      }

      if (ev.code !== 'ArrowUp' && ev.code !== 'ArrowDown') {
        return;
      }

      event.preventDefault();
      onInteract();

      const moveBy = ev.code === 'ArrowUp' ? -10 : 10;

      setTranslateBy((currentValue) => {
        let tentative = currentValue + moveBy;
        const newValue = clamp(
          tentative,
          LIMITS[color].min,
          LIMITS[color].max
        );

        lockedTranslateBy.current = newValue;

        return newValue;
      });
    },
    [color, reset, onInteract]
  );

  React.useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleDrag);
      window.removeEventListener('touchmove', handleDrag);
    };
  }, [handleDrag]);

  return (
    <Wrapper>
      <ToothpickOutline
        alt=""
        src={`${ROOT}/${color}-toothpick-outline.png`}
      />
      <WienerBottom
        alt=""
        src={`${ROOT}/${color}-wiener-bottom.png`}
        style={{
          bottom: initialPosition,
          transform: `translateY(${translateBy}px)`,
        }}
      />
      <Toothpick alt="" src={`${ROOT}/${color}-toothpick.png`} />
      <WienerHandle
        style={{
          bottom: initialPosition,
        }}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        onKeyDown={handleKeyDown}
        onDoubleClick={() => {
          reset();
        }}
      >
        <WienerTop
          draggable={false}
          alt=""
          src={`${ROOT}/${color}-wiener-top.png`}
          style={{
            transform: `translateY(${translateBy}px)`,
          }}
        />
        <VisuallyHidden>
          This button inclues a draggable image of a wiener which can
          be dragged up and down to change its vertical position.
        </VisuallyHidden>
      </WienerHandle>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
`;

const ToothpickOutline = styled.img`
  display: block;
  position: relative;
  width: 100%;
  pointer-events: none;
`;
const WienerBottom = styled.img`
  display: block;
  position: absolute;
  width: 100%;
  left: 0;
  right: 0;
  pointer-events: none;
`;
const Toothpick = styled.img`
  display: block;
  position: absolute;
  inset: 0px;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const WienerHandle = styled.button`
  display: block;
  position: absolute;
  width: 100%;
  left: 0;
  right: 0;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  &:focus {
    outline: none;
  }
`;

const WienerTop = styled.img`
  display: block;
  width: 100%;
  user-select: none;
  touch-action: none;

  ${WienerHandle}:focus-visible & {
    outline-color: Highlight;
    outline-color: -webkit-focus-ring-color;
    outline-style: auto;
    outline-width: 1px;
  }
`;
export default Wiener;
